/**
 * TopBar.js
 * 상단 네비게이션 바 - UI/UX 정책 기준서 완전 반영
 * - 사이드바 토글 (데스크탑)
 * - 다크/라이트 모드 전환
 * - 검색, 알림, 프로필
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  useTheme as useMuiTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Backdrop,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { Smartphone, Monitor, Mail, Shield } from 'react-feather';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import AnnouncementModal from '../components/AnnouncementModal';
import axios from 'axios';

const TopBar = ({ onDrawerToggle, onSidebarToggle, sidebarOpen = true, hideMobileMenu = false }) => {
  const muiTheme = useMuiTheme();
  const { isDark, toggleTheme, colors } = useTheme();
  const { isMobileView, toggleMobileView } = useMobileView();
  const { resetOnboarding } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  
  // 모바일 뷰일 때 또는 작은 화면일 때 햄버거 메뉴 표시 (hideMobileMenu가 false일 때만)
  const showMobileMenu = !hideMobileMenu && (isMobileView || isSmallScreen);
  
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginOTP, setLoginOTP] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpModalMessage, setOtpModalMessage] = useState('');
  const [otpModalType, setOtpModalType] = useState('success'); // 'success' or 'error'
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });
  const [todayMinutes, setTodayMinutes] = useState(0);

  // 실시간 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 오늘 업무시간 조회
  useEffect(() => {
    const fetchTodayWorkHours = async () => {
      if (!isLoggedIn) {
        setTodayMinutes(0);
        return;
      }
      
      try {
        const today = new Date().toISOString().split('T')[0];
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await axios.get('/api/v1/timelogs/my', {
          params: { date: today },
          headers: { Authorization: `Bearer ${token}` }
        });
        const logs = response.data || [];
        const total = logs.reduce((sum, log) => sum + (log.input_minute || 0), 0);
        setTodayMinutes(total);
      } catch (error) {
        console.error('오늘 업무시간 조회 실패:', error);
        setTodayMinutes(0);
      }
    };
    
    if (isLoggedIn) {
      fetchTodayWorkHours();
      // 5분마다 자동 새로고침
      const interval = setInterval(fetchTodayWorkHours, 5 * 60 * 1000);
      
      // 투입시간 입력/수정/삭제 시 즉시 업데이트
      window.addEventListener('timelogUpdated', fetchTodayWorkHours);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('timelogUpdated', fetchTodayWorkHours);
      };
    }
  }, [isLoggedIn]);

  // 앱 시작 시 로그인 상태 확인
  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setIsLoggedIn(loggedIn);
    if (!loggedIn) {
      setLoginModalOpen(true);
    }
  }, []);

  // 읽지 않은 공지사항 개수 조회
  const fetchUnreadAnnouncementCount = async () => {
    if (!isLoggedIn) {
      setUnreadAnnouncementCount(0);
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get('/api/v1/announcements/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadAnnouncementCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('읽지 않은 공지사항 개수 조회 실패:', error);
      setUnreadAnnouncementCount(0);
    }
  };

  // 로그인 상태 변경 시 및 주기적으로 읽지 않은 공지사항 개수 조회
  useEffect(() => {
    if (isLoggedIn) {
      fetchUnreadAnnouncementCount();
      fetchNotifications(); // 알림 목록도 조회
      // 30초마다 업데이트
      const interval = setInterval(() => {
        fetchUnreadAnnouncementCount();
        fetchNotifications();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      setUnreadAnnouncementCount(0);
      setNotifications([]);
      setUnreadNotificationCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // 알림 수신 이벤트 리스너
  useEffect(() => {
    const handleNotificationReceived = () => {
      fetchNotifications(); // 알림 수신 시 목록 새로고침
    };

    // 커스텀 이벤트 리스너 (포그라운드)
    window.addEventListener('notification-received', handleNotificationReceived);

    // Service Worker 메시지 리스너 (백그라운드)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'notification-received') {
          handleNotificationReceived();
        }
      });
    }

    return () => {
      window.removeEventListener('notification-received', handleNotificationReceived);
    };
  }, []);

  const handleMenuClose = () => {
    // 메뉴 닫기 (현재는 사용되지 않지만 handleLogout에서 호출됨)
  };

  // 알림 목록 조회
  const fetchNotifications = async () => {
    if (!isLoggedIn) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        console.warn('[TopBar] 알림 목록 조회 실패 - 토큰 없음');
        setNotifications([]);
        setUnreadNotificationCount(0);
        return;
      }
      
      console.log('[TopBar] 알림 목록 조회 시작: GET /api/v1/networking/notifications', {
        hasToken: !!token,
        tokenLength: token.length
      });
      
      const startTime = Date.now();
      const response = await axios.get('/api/v1/networking/notifications', {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000 // 10초 타임아웃
      });
      const elapsedTime = Date.now() - startTime;
      
      console.log(`[TopBar] 알림 목록 조회 성공 (${elapsedTime}ms):`, {
        알림수: response.data?.notifications?.length || 0,
        미읽음수: response.data?.unread_count || 0,
        status: response.status,
        hasData: !!response.data
      });
      
      setNotifications(response.data?.notifications || []);
      setUnreadNotificationCount(response.data?.unread_count || 0);
    } catch (error) {
      const elapsedTime = error.config ? (Date.now() - (error.config.startTime || Date.now())) : 0;
      
      // 상세 오류 로그
      const errorInfo = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.response?.data?.detail || error.message,
        url: error.config?.url,
        method: error.config?.method,
        elapsedTime: elapsedTime > 0 ? `${elapsedTime}ms` : 'N/A',
        errorCode: error.code,
        errorName: error.name
      };
      
      // 오류 타입별 상세 로그
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('[TopBar] 알림 API 타임아웃 오류 - 서버 응답 없음:', errorInfo);
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        console.error('[TopBar] 알림 API 네트워크 오류 - 연결 실패:', errorInfo);
      } else if (error.response?.status === 404) {
        console.warn('[TopBar] 알림 API 404 오류 - API가 배포되지 않았거나 엔드포인트가 존재하지 않음:', errorInfo);
      } else if (error.response?.status === 401) {
        console.warn('[TopBar] 알림 API 401 오류 - 인증 실패:', errorInfo);
      } else if (error.response?.status === 500) {
        console.error('[TopBar] 알림 API 500 오류 - 서버 오류:', errorInfo, error.response?.data);
      } else if (error.response?.status === 502) {
        console.error('[TopBar] 알림 API 502 오류 - Bad Gateway (백엔드 서버 다운):', errorInfo);
      } else if (error.response?.status === 503) {
        console.error('[TopBar] 알림 API 503 오류 - Service Unavailable:', errorInfo);
      } else {
        console.error('[TopBar] 알림 목록 조회 실패 (기타 오류):', errorInfo, error);
      }
      
      setNotifications([]);
      setUnreadNotificationCount(0);
    }
  };

  // 알림 읽음 처리
  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`/api/v1/networking/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications(); // 목록 새로고침
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error);
    }
  };

  // 알림 시간 포맷팅
  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return created.toLocaleDateString('ko-KR');
  };

  const handleNotificationsOpen = (event) => {
    setNotificationModalOpen(true);
    fetchNotifications(); // 모달 열 때 알림 목록 조회
  };

  const handleNotificationsClose = () => {
    setNotificationModalOpen(false);
  };

  const handleAnnouncementRead = () => {
    // 읽음 처리 후 카운트 업데이트
    fetchUnreadAnnouncementCount();
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleLogin = async () => {
    try {
      // TODO: 실제 API 호출로 변경
      // 임시: 이메일과 OTP 검증
      if (loginEmail && loginOTP) {
        // 로그인 성공
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userEmail', loginEmail);
        setIsLoggedIn(true);
        setLoginModalOpen(false);
        setLoginEmail('');
        setLoginOTP('');
        // 대시보드로 이동 (모바일/PC 뷰에 따라)
        const isMobile = document.body.classList.contains('mobile-view');
        if (isMobile) {
          navigate('/m/dashboard');
        } else {
          navigate('/p/dashboard');
        }
      }
    } catch (error) {
      console.error('로그인 실패:', error);
      alert('로그인에 실패했습니다. 이메일과 OTP를 확인해주세요.');
    }
  };

  const handleLogout = () => {
    // 모든 로그인 관련 정보 제거
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('employeeCode');
    localStorage.removeItem('employeeName');
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    localStorage.removeItem('departments');
    
    setIsLoggedIn(false);
    handleMenuClose();
    handleSettingsMenuClose();
    
    // 로그인 페이지로 리다이렉트
    navigate('/login', { replace: true });
  };

  const handleRequestOTP = async () => {
    if (!loginEmail) {
      setOtpModalMessage('이메일을 입력해주세요.');
      setOtpModalType('error');
      setOtpModalOpen(true);
      return;
    }
    try {
      // TODO: 실제 API 호출로 변경 - 회사 메일로 OTP 전송
      setOtpModalMessage(`${loginEmail}로 OTP가 전송되었습니다. 메일을 확인해주세요.`);
      setOtpModalType('success');
      setOtpModalOpen(true);
    } catch (error) {
      console.error('OTP 전송 실패:', error);
      setOtpModalMessage('OTP 전송에 실패했습니다. 다시 시도해주세요.');
      setOtpModalType('error');
      setOtpModalOpen(true);
    }
  };

  // handleSearch는 현재 사용되지 않음

  // 키보드 단축키 (Ctrl+K or Cmd+K)
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        // 검색창에 포커스
        const searchInput = document.querySelector('input[placeholder*="검색"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: isDark ? '#141414' : '#500B71',
        borderBottom: isDark ? '1px solid #333' : 'none',
        borderRadius: 0, // 상단 메뉴바 각진 모서리 (모바일 포함)
        borderTopLeftRadius: 0, // 상단 좌측 각진 모서리
        borderTopRightRadius: 0, // 상단 우측 각진 모서리
        borderBottomLeftRadius: 0, // 하단 좌측 각진 모서리
        borderBottomRightRadius: 0, // 하단 우측 각진 모서리
        '&.MuiAppBar-root': {
          borderRadius: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        '&.MuiPaper-root': {
          borderRadius: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
      }}
    >
      <Toolbar 
        sx={{ 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          minHeight: isMobileView ? '56px !important' : '64px !important',
          paddingLeft: isMobileView ? '16px !important' : '24px !important',
          paddingRight: isMobileView ? '8px !important' : '12px !important',
          paddingTop: isMobileView ? '4px !important' : '0px !important',
          paddingBottom: isMobileView ? '4px !important' : '0px !important',
          borderRadius: 0, // 둥근 모서리 제거 (모바일 포함)
          borderTopLeftRadius: 0, // 상단 좌측 각진 모서리
          borderTopRightRadius: 0, // 상단 우측 각진 모서리
          borderBottomLeftRadius: 0, // 하단 좌측 각진 모서리
          borderBottomRightRadius: 0, // 하단 우측 각진 모서리
          '&.MuiToolbar-root': {
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        {/* Mobile Menu Toggle - 모바일 뷰일 때 항상 표시 */}
        {showMobileMenu && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* 날짜/요일/시간 - 왼쪽 정렬 (PC/모바일 공통) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 500,
              textShadow: isDark
                ? '0 2px 8px rgba(0,0,0,0.45)'
                : '0 2px 8px rgba(80,11,113,0.45)',
              whiteSpace: 'nowrap',
            }}
          >
            {format(currentTime, 'yyyy/MM/dd', { locale: ko })}({format(currentTime, 'EEE', { locale: ko })}), {format(currentTime, 'hh:mm', { locale: ko })} {currentTime.getHours() < 12 ? '오전' : '오후'}
          </Typography>
          
          {/* 오늘 집계 시간 */}
          {isLoggedIn && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 300,
                  textShadow: isDark
                    ? '0 2px 8px rgba(0,0,0,0.45)'
                    : '0 2px 8px rgba(80,11,113,0.45)',
                  whiteSpace: 'nowrap',
                  opacity: 0.7,
                }}
              >
                |
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 500,
                  textShadow: isDark
                    ? '0 2px 8px rgba(0,0,0,0.45)'
                    : '0 2px 8px rgba(80,11,113,0.45)',
                  whiteSpace: 'nowrap',
                }}
              >
                오늘 투입시간 : {Math.floor(todayMinutes / 60)}시간 {todayMinutes % 60}분
              </Typography>
            </>
          )}
        </Box>

        {/* 오른쪽 영역: 아이콘들 */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1, mr: -2 }}>
          {/* Notifications */}
          <Tooltip title="알림">
            <IconButton 
              color="inherit" 
              onClick={handleNotificationsOpen}
              sx={{ mx: 0 }}
            >
              <Badge badgeContent={(unreadAnnouncementCount + unreadNotificationCount) > 0 ? (unreadAnnouncementCount + unreadNotificationCount) : null} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile */}
          <Tooltip 
            title={
              isLoggedIn 
                ? (localStorage.getItem('userEmail') || '사용자')
                : "로그인"
            }
          >
            <IconButton
              onClick={() => setLoginModalOpen(true)}
              size="small"
              sx={{ mx: 0 }}
            >
              <Avatar sx={{ 
                width: 31, 
                height: 31, 
                bgcolor: isDark ? '#00BFFF' : '#722ed1' 
              }}>
                {isLoggedIn ? (localStorage.getItem('userEmail')?.charAt(0).toUpperCase() || 'U') : '?'}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Settings Menu (바독판/벤또 아이콘) - 테마/화면유형/새로고침 */}
          <Tooltip title="설정">
            <IconButton 
              color="inherit" 
              onClick={handleSettingsMenuOpen}
              sx={{ mx: 0 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Settings Menu (바독판/벤또 아이콘) */}
        <Menu
          anchorEl={settingsMenuAnchor}
          open={Boolean(settingsMenuAnchor)}
          onClose={handleSettingsMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              minWidth: '180px',
              width: 'auto',
              maxWidth: '200px',
              marginRight: '8px',
              '& .MuiMenuItem-root': {
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                padding: '8px 16px',
                justifyContent: 'flex-start',
                '& .MuiListItemIcon-root': {
                  minWidth: '40px',
                  marginRight: '-4px',
                  '& svg': {
                    fontSize: '20px',
                  },
                },
              },
            }
          }}
        >
          <MenuItem onClick={() => { toggleTheme(); handleSettingsMenuClose(); }}>
            <ListItemIcon>
              {isDark ? <LightModeIcon sx={{ fontSize: '20px' }} /> : <DarkModeIcon sx={{ fontSize: '20px' }} />}
            </ListItemIcon>
            {isDark ? "라이트 모드" : "다크 모드"}
          </MenuItem>
          <MenuItem onClick={() => { 
            // 경로 매핑: PC ↔ 모바일
            const pathMapping = {
              '/p/dashboard': '/m/dashboard',
              '/m/dashboard': '/p/dashboard',
              '/p/job-time-input': '/m/log',
              '/m/log': '/p/job-time-input',
              '/p/todo-list': '/m/todo',
              '/m/todo': '/p/todo-list',
              '/p/admin': '/p/admin', // 모바일에서는 PC로 리다이렉트되므로 그대로 유지
              '/m/admin': '/p/admin',
            };
            
            // 현재 경로 확인
            const currentPath = location.pathname;
            const targetPath = pathMapping[currentPath] || (isMobileView ? '/p/dashboard' : '/m/dashboard');
            
            // 뷰 전환
            toggleMobileView();
            
            // 라우팅 변경
            navigate(targetPath, { replace: true });
            
            handleSettingsMenuClose();
          }}>
            <ListItemIcon>
              {isMobileView ? <Monitor size={20} /> : <Smartphone size={20} />}
            </ListItemIcon>
            {isMobileView ? "데스크탑 뷰로 전환" : "모바일 뷰로 전환"}
          </MenuItem>
          <MenuItem onClick={() => { window.location.reload(); handleSettingsMenuClose(); }}>
            <ListItemIcon>
              <RefreshIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            데이터 새로고침
          </MenuItem>
          <MenuItem onClick={() => { 
            resetOnboarding(); 
            handleSettingsMenuClose(); 
          }}>
            <ListItemIcon>
              <SchoolIcon sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            튜토리얼 다시보기
          </MenuItem>
          {isLoggedIn && (
            <>
              <Divider />
              <MenuItem onClick={() => { handleLogout(); handleSettingsMenuClose(); }}>
                <ListItemIcon>
                  <LogoutIcon sx={{ fontSize: '20px' }} />
                </ListItemIcon>
                로그아웃
              </MenuItem>
            </>
          )}
        </Menu>


        {/* 알림 모달 */}
        <Dialog
          open={notificationModalOpen}
          onClose={handleNotificationsClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: '16px',
            fontWeight: 600,
          }}>
            <NotificationsIcon />
            알림
          </DialogTitle>
          <DialogContent>
            {notifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: colors.secondaryText }}>
                <Typography variant="body2">알림이 없습니다.</Typography>
              </Box>
            ) : (
              <List>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.notification_id}
                    button
                    onClick={() => {
                      if (!notification.is_read) {
                        markNotificationRead(notification.notification_id);
                      }
                    }}
                    sx={{
                      backgroundColor: notification.is_read ? 'transparent' : (isDark ? 'rgba(138, 124, 255, 0.08)' : 'rgba(138, 124, 255, 0.04)'),
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(138, 124, 255, 0.12)' : 'rgba(138, 124, 255, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {!notification.is_read && (
                        <Badge badgeContent={1} color="error">
                          <NotificationsIcon />
                        </Badge>
                      )}
                      {notification.is_read && <NotificationsIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.body}
                      secondary={formatNotificationTime(notification.created_at)}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNotificationsClose} sx={{ fontSize: '12px' }}>
              닫기
            </Button>
          </DialogActions>
        </Dialog>

        {/* 로그인 모달 */}
        <Dialog
          open={loginModalOpen && !isLoggedIn}
          onClose={() => {
            // 로그인하지 않은 상태에서는 모달을 닫을 수 없음
            if (isLoggedIn) {
              setLoginModalOpen(false);
            }
          }}
          maxWidth={isMobileView ? "xs" : "sm"}
          fullWidth
          disableEscapeKeyDown={!isLoggedIn}
          BackdropComponent={Backdrop}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: isMobileView ? 2 : 3,
              backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
              padding: isMobileView ? 0 : 2,
              minWidth: isMobileView ? 'auto' : '480px',
              maxWidth: isMobileView ? '60%' : '600px',
              margin: isMobileView ? '0 20%' : 'auto',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: 1,
            fontSize: isMobileView ? '12px' : '16px',
            fontWeight: 600,
            pb: isMobileView ? 1 : 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Shield size={isMobileView ? 16 : 20} />
              <Typography sx={{ fontSize: isMobileView ? '12px' : '16px', fontWeight: 600 }}>
                로그인
              </Typography>
            </Box>
            <Typography sx={{ 
              fontSize: isMobileView ? '12px' : '16px',
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              fontWeight: 500,
            }}>
              {format(currentTime, 'yyyy/MM/dd', { locale: ko })}({format(currentTime, 'EEE', { locale: ko })}), {format(currentTime, 'hh:mm', { locale: ko })} {currentTime.getHours() < 12 ? '오전' : '오후'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: isMobileView ? 2 : 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              <TextField
                label="사용자 ID"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="example@company.com"
                fullWidth
                size={isMobileView ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={isMobileView ? 16 : 18} style={{ color: muiTheme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: isMobileView ? '12px' : '16px',
                  },
                  '& .MuiInputBase-root': {
                    fontSize: isMobileView ? '12px' : '16px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: isMobileView ? '12px' : '16px',
                  },
                }}
              />
              <Box>
                <TextField
                  label="OTP 번호"
                  type="text"
                  value={loginOTP}
                  onChange={(e) => setLoginOTP(e.target.value)}
                  placeholder="회사 메일로 받은 OTP 입력"
                  fullWidth
                  size={isMobileView ? "small" : "medium"}
                  sx={{
                    mb: 1.5,
                    '& .MuiInputLabel-root': {
                      fontSize: isMobileView ? '12px' : '16px',
                    },
                    '& .MuiInputBase-root': {
                      fontSize: isMobileView ? '12px' : '16px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: isMobileView ? '12px' : '16px',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size={isMobileView ? "small" : "medium"}
                  onClick={handleRequestOTP}
                  fullWidth
                  sx={{ 
                    fontSize: isMobileView ? '12px' : '16px', 
                    py: isMobileView ? 0.5 : 1 
                  }}
                >
                  OTP 번호 받기
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: isMobileView ? 2 : 3, pb: isMobileView ? 2 : 3 }}>
            <Button 
              onClick={handleLogin} 
              variant="contained"
              fullWidth
              disabled={!loginEmail || !loginOTP}
              size={isMobileView ? "small" : "medium"}
              sx={{ 
                fontSize: isMobileView ? '12px' : '16px',
                py: isMobileView ? 1 : 1.5,
                fontWeight: 600,
              }}
            >
              로그인
            </Button>
          </DialogActions>
        </Dialog>

        {/* 공지사항 알림 모달 */}
        <AnnouncementModal
          open={announcementModalOpen}
          onClose={() => setAnnouncementModalOpen(false)}
          onRead={handleAnnouncementRead}
        />

        {/* OTP 발송 알림 모달 */}
        <Dialog
          open={otpModalOpen}
          onClose={() => setOtpModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              backgroundColor: isDark ? colors.background : '#fff',
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {otpModalType === 'success' ? (
              <>
                <Mail size={20} style={{ color: colors.accent }} />
                <Typography variant="h6">OTP 발송 완료</Typography>
              </>
            ) : (
              <>
                <Alert severity="error" sx={{ p: 0, mr: 1 }} />
                <Typography variant="h6">OTP 발송 실패</Typography>
              </>
            )}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ color: colors.primaryText, mt: 1 }}>
              {otpModalMessage}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOtpModalOpen(false)}
              variant="contained"
              sx={{ backgroundColor: colors.accent }}
            >
              확인
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>
      
    </AppBar>
  );
};

export default TopBar;
